import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useCollect } from 'hooks';

import DayJs from 'utils/Dayjs';
import { WEBINAR_TYPES } from 'utils/Constants';

import Show from 'components/Show';
import Pill from 'components/Misc/Pill';
import Progress from 'components/Misc/Progress';
import Container from 'components/Misc/Container';
import { Title } from 'components/Misc/Typography';
import { Datepicker } from 'components/Misc/Form';

import { useAppStore } from 'store/useAppStore';

const Profile = () => {
	// Cache current object's id
	const { id } = useParams();

	// Use app's store
	const setTitle = useAppStore(state => state.setTitle);

	// Store request parameters
	const parameters = useMemo(() => ({
		eager: '[subject, course]'
	}), []);

	// Store requested webinar
	const [[webinar, progress], collect] = useCollect(`webinars/${id}`, parameters);

	// Find type info
	const type = useMemo(() => WEBINAR_TYPES.find(type => type.value === webinar?.type), [webinar]);

	// Store if webinar is done
	const hasEnded = useMemo(() => DayJs(webinar?.eventAt).isBefore(DayJs().subtract(2, 'hour')), [webinar]);
	const hasStarted = useMemo(() => DayJs().add(15, 'minute').isAfter(DayJs(webinar?.eventAt)), [webinar]);

	// Set page's title
	useEffect(() => setTitle(webinar?.title), [webinar]);

	// Collect webinar on mount and on parameter change
	useEffect(() => {
		(async () => await collect())();
	}, [collect]);

	return (
		<Container className="mx-auto max-w-screen-md p-4 space-y-4">
			{/* Show progress */}
			<Progress progress={progress} />

			{/* Show collected webinar */}
			<Show if={!progress && webinar?.id}>
				{/* Webinar's information */}
				<div className="flex flex-col space-y-2 md:flex-row md:items-center md:space-x-2 md:space-y-0">
					<Title
						title={webinar?.course?.title}
						subtitle={webinar?.subject?.title}
						className="flex-1 text-xl"
					/>

					<div className="flex space-x-2">
						<Pill
							name={type?.text}
							theme="informative"
						/>

						<Show if={webinar?.type === 'online'}>
							<Datepicker
								time
								readOnly
								value={webinar?.eventAt}
							/>
						</Show>
					</div>
				</div>

				{/* Show event date if webinar will be online */}
				<Show if={webinar?.type === 'online'}>
					{/* Information block after */}
					<Show if={hasEnded}>
						<div className="text-neutral-800 dark:text-white transition-colors">
							Vebinārs ir noslēdzies! Drīzumā tam būs pieejams ieraksts, kuru varēsi atrast dotā moduļa vebināru sarakstā!
						</div>
					</Show>

					{/* Information block before */}
					<Show if={!hasEnded && !hasStarted}>
						<div className="text-neutral-800 dark:text-white transition-colors">
							Vebināram varēs pieslēgties 15 minūtes pirms paredzētā laika!
						</div>
					</Show>
				</Show>

				{/* Show vod if webinar is recorded */}
				<Show if={webinar?.type === 'record' && webinar?.url}>
					<video controls className="w-full rounded-lg">
						<source src={webinar?.url} />
					</video>
				</Show>
			</Show>
		</Container>
	);
};

export default Profile;